<template>
  <div
    class="page--consolidated-report"
    :style="`min-height: ${height}px`"
  >
    <Header :status="currentStatus" pageType="new" />

    <transition name="fade" v-if="isSuccess || isFailed">
      <div v-if="alert.visibility" class="form__alert">
        <v-icon color="error">warning</v-icon>
        <span>{{ alert.message }}</span>
      </div>
    </transition>

    <div class="form-fw__content">
      <fieldset class="form__container px-0">
        <form class="form--custom" novalidate @submit.prevent="createdData">
          <Processing :isProcessing="isProcessing" v-if="isLoading" />

          <FormAlert :alert="alert" v-if="isSuccess || isFailed" />

          <TryAgain :isFailed="isFailed" @tryAgain="getCompanies" />

          <div class="period__container" v-show="isSuccess && step === 1">
            <v-layout class="px-4">
              <v-flex>
                <h3 class="card__label my-3">{{ $t(`${namespace}.selectPeriod`) }}</h3>
              </v-flex>
            </v-layout>

            <v-layout class="px-4">
              <v-flex sm6>
                <div class="pr-2--desktop period__calendar__container">
                  <label>{{ $t(`${namespace}.fieldStartDateLabel`) }}*</label>
                  <v-text-field
                    v-model="filters.startDate"
                    ref="startDate"
                    color="accent"
                    :name="$t(`${namespace}.fieldStartDateName`)"
                    :placeholder="$t(`${namespace}.fieldStartDatePlaceholder`)"
                    mask="##/##/####"
                    return-masked-value
                    append-icon="today"
                    v-validate="{ required: true, isValidDate: true }"
                    :error-messages="errors.collect($t(`${namespace}.fieldStartDateName`))"
                    :data-vv-name="$t(`${namespace}.fieldStartDateName`)"
                    required
                    flat
                    outline
                    solo
                    @click:append="openCalendar('startDateDialog')"
                  />
                  <v-dialog
                    v-model="startDateDialog"
                    :open-on-click="false"
                    :close-on-content-click="false"
                    lazy
                    full-width
                    width="290"
                  >
                    <v-date-picker
                      v-model="startDate"
                      :locale="currentLocale"
                      :max="today"
                      color="accent"
                      @change="(val) => closeCalendar('startDateDialog', 'startDate', val)"
                    />
                  </v-dialog>
                </div>
              </v-flex>

              <v-flex sm6>
                <div class="pr-2--desktop">
                  <label>{{ $t(`${namespace}.fieldEndDateLabel`) }}*</label>
                  <v-text-field
                    v-model="filters.endDate"
                    ref="endDate"
                    color="accent"
                    :name="$t(`${namespace}.fieldEndDateName`)"
                    :placeholder="$t(`${namespace}.fieldEndDatePlaceholder`)"
                    mask="##/##/####"
                    return-masked-value
                    append-icon="today"
                    v-validate="{ required: true, isValidDate: true }"
                    :error-messages="errors.collect($t(`${namespace}.fieldEndDateName`))"
                    :data-vv-name="$t(`${namespace}.fieldEndDateName`)"
                    required
                    flat
                    outline
                    solo
                    @click:append="openCalendar('endDateDialog')"
                  />
                  <v-dialog
                    v-model="endDateDialog"
                    :open-on-click="false"
                    :close-on-content-click="false"
                    lazy
                    full-width
                    width="290"
                  >
                    <v-date-picker
                      v-model="endDate"
                      :locale="currentLocale"
                      :max="today"
                      color="accent"
                      @change="(val) => closeCalendar('endDateDialog', 'endDate', val)"
                    />
                  </v-dialog>
                </div>
              </v-flex>
            </v-layout>
            <v-layout class="px-4">
              <v-flex>
                <h3 class="card__label">{{ $t(`${namespace}.selectCompanies`) }}</h3>
              </v-flex>
            </v-layout>

            <v-layout class="px-4 py-4">
              <v-flex>
                <Search :status="currentStatus" @change="updateFilters" />
              </v-flex>
            </v-layout>

            <v-layout>
              <v-flex>
                <Processing :isProcessing="isProcessing" v-if="isSearching" />

                <TableSelect
                  v-show="!isSearching"
                  :items="companies"
                  :status="currentStatus"
                  :pagination="pagination"
                  :selectedAll="selectedAllCompanies"
                  @selectAllChange="selectedAllCompanies = $event"
                  @selectedItems="selectedCompanies = $event"
                  @goToPage="updatePage"
                />
              </v-flex>
            </v-layout>
          </div>

          <ScheduledReport v-show="isSuccess && step === 2" />

          <Footer :isProcessing="isProcessing" :step="step" />
        </form>
      </fieldset>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import {
  STATUS_IS_LOADING,
  STATUS_SUCCESS,
  STATUS_FAILED
} from 'utils/constants'
import { mapActions } from 'vuex'
import TableSelect from 'components/company/TableSelect'
import Header from 'components/reports/consolidated/Header'
import Footer from 'components/reports/consolidated/Footer'
import Processing from 'components/Processing'
import FormAlert from 'components/reports/consolidated/FormAlert'
import ScheduledReport from 'components/reports/consolidated/ScheduledReport'
import Search from 'components/company/Search'
import TryAgain from 'components/TryAgain'

export default {
  name: 'ConsolidatedReportPage',
  components: {
    TableSelect,
    Header,
    Footer,
    Search,
    Processing,
    FormAlert,
    ScheduledReport,
    TryAgain
  },
  data () {
    return {
      namespace: 'Views.ConsolidatedReportPage',

      currentStatus: null,
      companies: [],
      selectedCompanies: [],
      selectedAllCompanies: false,
      step: 1,

      isProcessing: false,
      isSearching: false,

      // alert
      alert: {
        visibility: false,
        message: false
      },

      // dialogs
      startDateDialog: false,
      endDateDialog: false,

      // temp props
      startDate: null,
      endDate: null,
      maxPeriod: 12,

      filters: {
        startDate: null,
        endDate: null,
        search: null
      },

      pagination: {
        totalElements: 0,
        totalPages: 0,
        currentPage: 1,
        itemsPerPage: 25
      }
    }
  },
  computed: {
    today () {
      return moment().format('YYYY-MM-DD')
    },

    height () {
      return document.body.offsetHeight - 64
    },

    currentLocale () {
      return this.$i18n.locale.split('_').join('-')
    },

    isLoading () {
      return this.currentStatus === STATUS_IS_LOADING
    },

    isSuccess () {
      return this.currentStatus === STATUS_SUCCESS
    },

    isFailed () {
      return this.currentStatus === STATUS_FAILED
    }
  },
  mounted () {
    this.getCompanies()
  },
  methods: {
    ...mapActions({
      errorAlert: 'snackbarAlert/error'
    }),

    updateFilters (filters) {
      this.filters = {
        ...filters,
        startDate: this.filters.startDate,
        endDate: this.filters.endDate
      }
      this.isSearching = true
      this.resetPagination()
      this.getCompanies()
    },

    updatePagination (response, itemsPerPage) {
      const totalPagesCalc = Math.ceil(response.length / itemsPerPage)
      const totalPages = totalPagesCalc > 0 ? totalPagesCalc : 1

      this.pagination = {
        ...this.pagination,
        totalElements: response.length,
        totalPages
      }
    },

    resetPagination () {
      this.pagination = {
        totalElements: 0,
        totalPages: 0,
        currentPage: 1,
        itemsPerPage: 25
      }
    },

    alertError ({ visibility, message }) {
      this.alert = { visibility, message }
    },

    convertDateToEnFormat (str) {
      return str
        .split('/')
        .reverse()
        .join('-')
    },

    convertDateToPtBrFormat (str) {
      return str
        .split('-')
        .reverse()
        .join('/')
    },

    openCalendar (dialogProp) {
      this[dialogProp] = true
    },

    closeCalendar (dialogProp, prop, val) {
      this[dialogProp] = false

      if (val) {
        this[prop] = val
        this.filters[prop] = this.convertDateToPtBrFormat(val)
        this.$refs[prop].focus()
      }
    },

    async getCompanies () {
      this.companies = []
      this.currentStatus = this.isSearching ? STATUS_SUCCESS : STATUS_IS_LOADING

      try {
        const { currentPage, itemsPerPage } = this.pagination
        const { search } = this.filters
        const { data: res } = await this.$apiService.getCompany(
          { page: currentPage },
          { limit: itemsPerPage },
          { search }
        )
        this.companies = res.data
        this.updatePagination(res, itemsPerPage)
        this.isSearching = false
        this.currentStatus = STATUS_SUCCESS
      } catch (err) {
        console.log('Error', err)
        this.isSearching = false
        this.currentStatus = STATUS_FAILED
      }
    },

    validPeriod () {
      const startDate = `${this.startDate}T00:00`
      const endDate = `${this.endDate}T00:00`

      const todayMilliseconds = new Date().getTime()
      const startDateMilliseconds = new Date(startDate).getTime()
      const endDateMilliseconds = new Date(endDate).getTime()

      const monthsDiff = moment(endDate).diff(moment(startDate), 'months', true)

      if (endDateMilliseconds > todayMilliseconds) {
        this.alertError({
          visibility: true,
          message: this.$t(`${this.namespace}.endDateLimitError`)
        })

        this.$refs['endDate'].focus()
        return false
      }

      if (endDateMilliseconds <= startDateMilliseconds) {
        this.alertError({
          visibility: true,
          message: this.$t(`${this.namespace}.periodRangeError`)
        })

        this.$refs['endDate'].focus()
        return false
      }

      if (monthsDiff > this.maxPeriod) {
        this.alertError({
          visibility: true,
          message: this.$t(`${this.namespace}.periodRangeLimitError`).replace(
            '[MAX_PERIOD]',
            this.maxPeriod
          )
        })

        return false
      }

      return true
    },

    async createdData () {
      try {
        this.alertError({ visibility: false, message: null })
        const isValid = await this.$validator.validateAll()

        if (!this.selectedCompanies.length) {
          this.alertError({
            visibility: true,
            message: this.$t(`${this.namespace}.noCompanySelected`)
          })
          return
        }

        if (isValid && this.validPeriod() && this.selectedCompanies.length) {
          this.isProcessing = true
          this.currentStatus = STATUS_IS_LOADING
          const startDate = `${this.startDate}`
          const endDate = `${this.endDate}`
          const companiesId = this.selectedAllCompanies
            ? []
            : this.selectedCompanies
          const selectedAllCompanies = this.selectedAllCompanies

          await this.$apiService.exportConsolidatedReport({
            startDate,
            endDate,
            companiesId,
            selectedAllCompanies
          })
          this.isProcessing = false
          this.currentStatus = STATUS_SUCCESS
          this.step = 2
        }
      } catch (err) {
        console.log('ERROR', err)
        this.isProcessing = false
        this.currentStatus = STATUS_SUCCESS

        if (err.response.status === 409) {
          this.alertError({
            visibility: true,
            message: this.$t(
              `${this.namespace}.exportConsolidatedReportFilterError`
            )
          })
          return
        }
        this.errorAlert(
          this.$t(`${this.namespace}.exportConsolidatedReportError`)
        )
      }
    },

    updatePage (page) {
      scrollTo(0, 0)

      this.pagination = {
        ...this.pagination,
        currentPage: page
      }

      this.isSearching = true
      this.getCompanies()
    }
  },
  watch: {
    'filters.startDate' (val) {
      this.alertError({ visibility: false, message: null })

      if (val && val.length === 10) {
        this.startDate = this.convertDateToEnFormat(val)
        this.validPeriod()
      }
    },

    'filters.endDate' (val) {
      this.alertError({ visibility: false, message: null })

      if (val && val.length === 10) {
        this.endDate = this.convertDateToEnFormat(val)
        this.validPeriod()
      }
    },

    selectedCompanies (val) {
      if (val.length > 100) {
        this.selectedCompanies.splice(100, val.length - 100)
        this.alertError({
          visibility: true,
          message: this.$t(`${this.namespace}.companiesExceeded`)
        })
      }

      if (val.length < 100) {
        this.alertError({ visibility: false, message: null })
      }
    }
  },
  created () {
    moment.locale(this.currentLocale)
    this.$validator.extend('isValidDate', {
      getMessage: field => this.$t(`${this.namespace}.invalidDate`),
      validate: value => {
        if (!value || value.length < 10) return false
        return moment(this.convertDateToEnFormat(value))._isValid
      }
    })
  }
}
</script>

<style lang="scss" scoped>
.card__label {
  font-family: 'Montserrat', sans-serif;
  color: #4a4b4a;
  font-size: 14px;
  font-weight: bold;
  line-height: 18px;
}
</style>
